import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { FaTimes, FaChevronDown, FaChevronUp, FaShoppingCart } from 'react-icons/fa';
import { useCart } from './CartContext';


export default function NavOverlay({ isOpen, onClose }) {
  const { cartCount } = useCart();
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const navigate = useNavigate();


  const handleLinkClick = (path) => {
    onClose();
    navigate(path);
  };


  const menuItems = [
    { title: 'My Account', path: '/profile' },
    { title: 'Wishlist', path: '/profile?tab=wishlist' },
    { title: 'My Cart', path: '/cart', showCartCount: true },
    { 
      title: 'Products', 
      path: '/shop',
      isExpandable: true,
      subItems: [
        { name: 'All Products', path: '/shop' },
        { name: 'Necklaces', path: '/shop/necklaces' }, 
        { name: 'Earrings', path: '/shop/earrings' },
        { name: 'Bracelets', path: '/shop/bracelets' },
        { name: 'Combos', path: '/shop/combos' },
        { name: 'Apparel', path: '/shop/apparel' },
      ]
    },
    { title: 'Gifting Hub', path: '/gifts' },
    { title: 'Contact Us', path: '/contact' },
    { title: 'Journal', path: '/journal' },
    { title: 'FAQ', path: '/faq' }
  ];



  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="nav-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 bg-black/60 z-[60] backdrop-blur-sm flex justify-end"
          onClick={onClose}
        >
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: [0.165, 0.84, 0.44, 1] }}
            className="w-full md:w-[500px] h-full bg-[#fff0f3] shadow-2xl p-8 overflow-y-auto"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation Menu"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-12">
              <span className="text-sm font-bold tracking-widest uppercase text-gray-500">Menu</span>
              <button 
                onClick={onClose} 
                aria-label="Close Navigation Menu"
                className="text-2xl text-gray-800 hover:text-rose-600 transition-colors"
              >
                <FaTimes />
              </button>
            </div>


            <div className="space-y-4">
              {menuItems.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + index * 0.1 }}
                  className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden"
                >
                  {item.isExpandable ? (
                    <div>
                      <div className="flex">
                        {/* Clickable Products title - takes 80% width */}
                        <Link 
                          to={item.path}
                          onClick={onClose}
                          className="flex-1 flex items-center p-6 text-left group"
                          aria-label={`Go to ${item.title}`}
                        >
                          <span className="text-2xl font-serif text-gray-900 group-hover:text-rose-600 transition-colors">{item.title}</span>
                        </Link>
                        
                        {/* Dropdown toggle button - takes 20% width */}
                        <button 
                          onClick={() => setIsProductsOpen(!isProductsOpen)}
                          className="px-6 flex items-center justify-center border-l border-gray-100 hover:bg-rose-50 transition-colors"
                          aria-label={isProductsOpen ? "Collapse Products Menu" : "Expand Products Menu"}
                          aria-expanded={isProductsOpen}
                        >
                          {isProductsOpen ? <FaChevronUp className="text-rose-500" /> : <FaChevronDown className="text-gray-400" />}
                        </button>
                      </div>
                      
                      <AnimatePresence>
                        {isProductsOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="bg-rose-50 border-t border-rose-100"
                          >
                            {item.subItems.map((sub) => (
                              <Link
                                key={sub.name}
                                to={sub.path}
                                onClick={onClose}
                                className="block w-full text-left py-3 px-8 text-lg font-serif text-gray-700 hover:text-rose-600 hover:bg-rose-100 transition-colors"
                              >
                                {sub.name}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link 
                      to={item.path}
                      onClick={onClose}
                      className="w-full flex justify-between items-center p-6 text-left group"
                      aria-label={`Go to ${item.title}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl font-serif text-gray-900 group-hover:text-rose-600 transition-colors">{item.title}</span>
                        {item.showCartCount && cartCount > 0 && (
                          <motion.span
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="bg-rose-500 text-white text-xs font-bold min-w-[20px] h-[20px] flex items-center justify-center rounded-full px-1 shadow-sm"
                          >
                            {cartCount > 99 ? '99+' : cartCount}
                          </motion.span>
                        )}
                      </div>
                      <span className="text-gray-300 group-hover:text-rose-400 transition-colors">→</span>
                    </Link>
                  )}
                </motion.div>
              ))}
            </div>
            
            <div className="mt-12 pt-8 border-t border-gray-200 text-center">
               <p className="text-gray-500 text-sm mb-4">Follow us</p>
               <div className="flex justify-center space-x-6 text-gray-400">
                  <a href="https://www.instagram.com/serastore.in" className="hover:text-rose-500 transition-colors">Instagram</a>
                  <a href="https://www.pinterest.com/serastore/" target="_blank" rel="noopener noreferrer" className="hover:text-rose-500 transition-colors">Pinterest</a>
               </div>
            </div>


          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
